import React, { useEffect, useState } from 'react';
import {
  X, User, Mail, Lock, BadgeCheck, ToggleLeft, ToggleRight
} from 'lucide-react';
import { authservice } from '../../../../components/api/authservice';

interface Props {
  onClose: () => void;
  onSubmit: () => void;
  userId?: string;
}
const tenantId = '6a6eee6f-75d7-4479-892d-80b5f225e004';
const AddUserModal: React.FC<Props> = ({ onClose, onSubmit, userId }) => {
  const isEditMode = !!userId;
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    roleID: '',
    isActive: true,
    tenantId: '', // optional, backend handles
  });
  const [error, setError] = useState('');


  useEffect(() => {
    if (isEditMode) {
      authservice.getSingleUser(tenantId, userId!).then(res => {
        const { user } = res;
        setForm({
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          password: '', // don't prefill
          roleID: user.roleID || '',
          isActive: user.isActive,
          tenantId: user.tenantId || '',
        });
      }).catch(console.error);
    }
  }, [userId]);

  const handleChange = (field: string, value: string | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    if (!form.firstName || !form.lastName || !form.email || !form.roleID) {
      return 'All fields are required.';
    }
    if (!isEditMode && form.password.length < 8) {
      return 'Password must be at least 8 characters.';
    }
    return '';
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }

    try {
      if (isEditMode) {
        await authservice.editUser({
          id: userId,
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName,
          tenantId: form.tenantId,
          roleID: form.roleID,
          isactive:form.isActive,
        });
      } else {
        await authservice.registerUser({
          email: form.email,
          password: form.password,
          firstName: form.firstName,
          lastName: form.lastName,
          tenant_id: form.tenantId,
          isactive:form.isActive,
          createdBy: '3fa85f64-5717-4562-b3fc-2c963f66afa6', // placeholder
          roleID: form.roleID,
        });
      }
      onSubmit();
    } catch (err) {
      setError('Error submitting form. Check console for details.');
      console.error(err);
    }
  };
  const [roles, setRoles] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    // Load roles on open
    authservice.getRoles()
      .then(res => {
        const parsed = (res || []).map((r: any) => ({
          id: r.roleId,
          name: r.roleName || 'Unnamed Role',
        }));
        setRoles(parsed);
      })
      .catch(err => console.error('Failed to fetch roles', err));
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4 text-[12px] font-sans">
      <div className="bg-neutral-900 text-white rounded-2xl shadow-2xl ring-1 ring-neutral-700 w-full max-w-md px-6 py-6 relative border border-neutral-800">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-200 transition">
          <X className="w-4 h-4" />
        </button>

        <h3 className="text-base font-semibold mb-5 text-white">{isEditMode ? 'Edit User' : 'Add New User'}</h3>

        {/* First Name */}
        <label className="block mb-1 text-gray-300">First Name</label>
        <div className="flex items-center gap-2 bg-neutral-800 border border-neutral-700 rounded-md px-3 py-2 mb-4">
          <User className="w-4 h-4 text-gray-400" />
          <input type="text" value={form.firstName} onChange={(e) => handleChange('firstName', e.target.value)}
            className="bg-transparent text-white text-sm flex-1 outline-none placeholder:text-gray-500"
            placeholder="John" />
        </div>

        {/* Last Name */}
        <label className="block mb-1 text-gray-300">Last Name</label>
        <div className="flex items-center gap-2 bg-neutral-800 border border-neutral-700 rounded-md px-3 py-2 mb-4">
          <User className="w-4 h-4 text-gray-400" />
          <input type="text" value={form.lastName} onChange={(e) => handleChange('lastName', e.target.value)}
            className="bg-transparent text-white text-sm flex-1 outline-none placeholder:text-gray-500"
            placeholder="Doe" />
        </div>

        {/* Email */}
        <label className="block mb-1 text-gray-300">Email</label>
        <div className="flex items-center gap-2 bg-neutral-800 border border-neutral-700 rounded-md px-3 py-2 mb-4">
          <Mail className="w-4 h-4 text-gray-400" />
          <input type="email" value={form.email} onChange={(e) => handleChange('email', e.target.value)}
            className="bg-transparent text-white text-sm flex-1 outline-none placeholder:text-gray-500"
            placeholder="john@example.com" />
        </div>

        {/* Password */}
        {!isEditMode && (
          <>
            <label className="block mb-1 text-gray-300">Password</label>
            <div className="flex items-center gap-2 bg-neutral-800 border border-neutral-700 rounded-md px-3 py-2 mb-4">
              <Lock className="w-4 h-4 text-gray-400" />
              <input type="password" value={form.password} onChange={(e) => handleChange('password', e.target.value)}
                className="bg-transparent text-white text-sm flex-1 outline-none placeholder:text-gray-500"
                placeholder="********" />
            </div>
          </>
        )}

        {/* Role */}
        <label className="block mb-1 text-gray-300">Role</label>
        <div className="flex items-center bg-neutral-800 border border-neutral-700 rounded-md px-3 py-2 mb-4">
          <BadgeCheck className="w-4 h-4 text-gray-400 mr-2" />
          <select value={form.roleID} onChange={(e) => handleChange('roleID', e.target.value)}
            className="bg-transparent text-white text-sm flex-1 outline-none">
            <option value="">Select Role</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id} className="bg-neutral-900 text-white">
                {role.name}
              </option>
            ))}
          </select>
        </div>

        {/* Active Status */}
        <div className="flex items-center justify-between mt-4">
          <label className="text-gray-300 text-sm">Active</label>
          <button onClick={() => handleChange('isActive', !form.isActive)} className="text-gray-400 hover:text-white transition">
            {form.isActive ? <ToggleRight className="w-5 h-5 text-green-400" /> : <ToggleLeft className="w-5 h-5" />}
          </button>
        </div>

        {/* Error */}
        {error && <p className="text-red-400 mt-3 text-xs">{error}</p>}

        {/* Submit */}
        <div className="mt-6 text-right">
          <button onClick={handleSubmit} className="bg-indigo-600 hover:bg-indigo-500 px-4 py-2 rounded-md text-white text-sm font-medium shadow-sm transition">
            {isEditMode ? 'Update User' : 'Add User'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddUserModal;
